/* eslint-disable max-len */
import { useCallback, useEffect, useRef, useState } from 'react'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { calculateChecksum, uploadToDirectUrl } from 'Pods/ThreeDStudio/Hooks/directUpload'
import { threeDStudioStore, useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { clearModelSettings, setInputThreeD } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import {
  openSignInModal,
  setActiveMobilePanel,
  setIsModelSettingsOpen,
  setIsSidebarOpen,
  setIsViewerSidebarOpen,
  setIsModelUploading,
} from 'Pods/ThreeDStudio/Store/actions/ui'
import { MOBILE_PANEL } from 'Pods/ThreeDStudio/Store/constants'

import useBreakpoint from 'Utils/useBreakpoint'

const ACCEPTED_EXTENSIONS = ['glb', 'gltf', 'fbx', 'obj', 'stl']
const MAX_FILE_SIZE = 200 * 1024 * 1024
// Keep in sync with the 0.3s width transition in _viewer_sidebar.scss. Parsing
// the model blocks the main thread, so loading must wait until the sidebar
// open animation has finished or the animation janks.
const SIDEBAR_ANIMATION_MS = 300
// Breakpoints where the workspace collapses into tabs - keep in sync with the
// media-breakpoint-down(md) rule on .mobile-tab-bar.
const MOBILE_BREAKPOINTS = ['xs', 'sm', 'md']

const waitForSidebarAnimation = () =>
  new Promise((resolve) => {
    setTimeout(resolve, SIDEBAR_ANIMATION_MS)
  })

function getFileExtension(filename) {
  const parts = String(filename || '').split('.')
  if (parts.length < 2) return ''
  return parts[parts.length - 1].toLowerCase()
}

async function getFileMetadata(file) {
  const checksum = await calculateChecksum(file)
  return ({
    checksum,
    filename: file.name,
    content_type: file.type || 'application/octet-stream',
    byte_size: file.size,
    metadata: {},
  })
}

export default function useThreeDModelUploader() {
  const inputRef = useRef(null)
  const isModelUploading = useThreeDStudioStore(state => state.ui?.isModelUploading)
  const [blobUrl, setBlobUrl] = useState(null)
  const { postData } = useApiPost()
  const { isCurrentUser: isLoggedIn } = usePageConfig() || {}
  const location = useLocation()
  const navigate = useNavigate()
  const breakpoint = useBreakpoint()
  const isMobileLayout = MOBILE_BREAKPOINTS.includes(breakpoint)

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [blobUrl])

  const processFile = useCallback((file) => {
    const extension = getFileExtension(file?.name)
    if (!file || !ACCEPTED_EXTENSIONS.includes(extension)) {
      window.showNotification(
        `Please select a 3D model file (${ACCEPTED_EXTENSIONS.join(', ').toUpperCase()}).`,
        { type: 'error', autohide: true },
      )
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      window.showNotification('File must be 200MB or smaller.', { type: 'error', autohide: true })
      return
    }

    setBlobUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev)
      }
      return null
    })
    // The upload survives the route change: the components that mount this
    // hook (Sidebar, MainPanel) stay mounted on the generate page.
    const wasViewerSidebarOpen = threeDStudioStore.getState().ui?.isViewerSidebarOpen
    if (location?.pathname !== '/ai') {
      navigate('/ai')
      clearModelSettings()
      // clearModelSettings re-opens the model settings panel for fresh-generation
      // flows; the upload only shows the viewer banner, so keep it collapsed to
      // avoid popping the 3D generation panel open mid-upload.
      setIsModelSettingsOpen(false)
      // clearModelSettings also force-closes the viewer sidebar. If it was open
      // (a model was already opened), reopen it in the same synchronous tick so
      // it never visibly closes-then-reopens once the upload completes.
      if (wasViewerSidebarOpen) {
        setIsViewerSidebarOpen(true)
      }
    }
    if (isMobileLayout) {
      setIsSidebarOpen(false)
      setActiveMobilePanel(MOBILE_PANEL.VIEWER)
    }
    setIsModelUploading(true)

    const createGeneratorAsset = (blob) =>
      new Promise((resolve, reject) => {
        postData({
          path: '/api/internal/generator/assets',
          data: { blob, asset_type: '3d_model' },
          onSuccess: resolve,
          onError: reject,
        })
      })

    getFileMetadata(file)
      .then(blob => createGeneratorAsset(blob))
      .then(async response => {
        const { id, direct_upload: directUpload } = response.data
        if (!directUpload?.url || !directUpload?.headers) {
          throw new Error('Invalid direct upload response')
        }
        await uploadToDirectUrl(directUpload.url, directUpload.headers, file)
        return { id }
      })
      .then(async ({ id }) => {
        const wasSidebarOpen = threeDStudioStore.getState().ui?.isViewerSidebarOpen
        setIsViewerSidebarOpen(true)
        if (!wasSidebarOpen) {
          await waitForSidebarAnimation()
        }
        const newBlobUrl = URL.createObjectURL(file)
        const format = getFileExtension(file.name)
        setInputThreeD(id, newBlobUrl, file.size, format)
        setBlobUrl((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev)
          }
          return newBlobUrl
        })
        if (inputRef.current) {
          inputRef.current.value = ''
        }
      })
      .catch(() => {
        window.showNotification('Upload failed. Please try again.', { type: 'error', autohide: true })
      })
      .finally(() => {
        setIsModelUploading(false)
      })
  }, [postData, location?.pathname, navigate, isMobileLayout])

  const onFileChange = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.target?.files?.[0]
    if (file) {
      processFile(file)
    }
  }, [processFile])

  const onFileClick = useCallback(() => {
    // Uploading creates a library model, which requires an account. Prompt a
    // logged-out user to sign up instead of opening the file picker.
    if (!isLoggedIn) {
      openSignInModal('signup')
      return
    }
    if (inputRef.current) {
      inputRef.current.click()
    }
  }, [isLoggedIn])

  return {
    acceptedExtensions: ACCEPTED_EXTENSIONS,
    inputRef,
    isLoading: isModelUploading,
    onFileChange,
    onFileClick,
  }
}
